// Pure geometry for locating one point inside a raster source. No mapbox, no DOM, no fetch —
// every decision here is testable in node, which is the point: the file that does touch the
// network contains no decisions at all.

export interface IPixelRect {
  x: number
  y: number
  width: number
  height: number
}

export interface ITileAddress {
  x: number
  y: number
  // Where the point falls INSIDE that tile, 0..1.
  fx: number
  fy: number
}

// Mapbox reasons in 512px tiles, so a 256px source is addressed one zoom level deeper. This
// mirrors Transform.coveringZoomLevel — matching it is what makes our fetch an HTTP cache hit
// on the tile mapbox already pulled to draw, which is the whole basis of the cost model. Get
// it wrong and the pixel is still right, just at cache-miss latency.
export function coveringZoom(
  mapZoom: number,
  tileSize: number,
  minzoom: number,
  maxzoom: number,
): number | null {
  const z = Math.round(mapZoom + Math.log2(512 / tileSize))
  // Below its minzoom the source draws nothing, so there is nothing to composite and nothing
  // worth fetching. Above its maxzoom mapbox overscales the deepest tile; so do we.
  if (z < minzoom) return null
  return Math.min(Math.max(z, 0), maxzoom)
}

// Mirrors TileBounds.contains: mapbox renders every tile the bbox INTERSECTS, so testing the
// point alone would drop ground that is painted.
export function boundsCoverTileAt(
  bounds: [number, number, number, number],
  lng: number,
  lat: number,
  z: number,
): boolean {
  const [west, south, east, north] = bounds
  const worldSize = Math.pow(2, z)
  const x = Math.floor(mercatorX(lng) * worldSize)
  const y = Math.floor(mercatorY(lat) * worldSize)
  return (
    x >= Math.floor(mercatorX(west) * worldSize) &&
    x < Math.ceil(mercatorX(east) * worldSize) &&
    y >= Math.floor(mercatorY(north) * worldSize) &&
    y < Math.ceil(mercatorY(south) * worldSize)
  )
}

export function mercatorX(lng: number): number {
  return (lng + 180) / 360
}

export function mercatorY(lat: number): number {
  const phi = (lat * Math.PI) / 180
  return 0.5 - Math.log(Math.tan(Math.PI / 4 + phi / 2)) / (2 * Math.PI)
}

export function lngOfMercatorX(x: number): number {
  return x * 360 - 180
}

export function latOfMercatorY(y: number): number {
  return (Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) * 180) / Math.PI
}

export function tileAt(
  lng: number,
  lat: number,
  z: number,
  scheme: 'xyz' | 'tms' = 'xyz',
): ITileAddress {
  const n = Math.pow(2, z)
  const gx = mercatorX(lng) * n
  const gy = mercatorY(lat) * n
  const x = Math.floor(gx)
  const yTop = Math.floor(gy)
  return {
    x,
    // TMS counts rows from the bottom; WMTS TileRow and XYZ both count from the top.
    y: scheme === 'tms' ? n - 1 - yTop : yTop,
    fx: gx - x,
    fy: gy - yTop,
  }
}

// A probe the size of the label's own glyph band, expressed in the source's pixels.
//
// Sizing it to the text is what the canvas sampler got wrong — the frame it read contained the
// label's own casing, while a source tile contains none — and it is what stops a single dark
// shingle flipping the ink of a label spanning a mixed roof.
//
// A probe straddling a tile edge is CLIPPED, not split: it moves the effective centre by a few
// pixels for a small minority, where splitting would multiply the fetch count.
export function probeRect(
  fx: number,
  fy: number,
  tileSize: number,
  probeWidthPx: number,
  probeHeightPx: number,
  z: number,
  mapZoom: number,
): IPixelRect {
  const sourcePxPerScreenPx =
    (tileSize * Math.pow(2, z)) / (512 * Math.pow(2, mapZoom))
  const width = Math.min(
    tileSize,
    Math.max(1, Math.round(probeWidthPx * sourcePxPerScreenPx)),
  )
  const height = Math.min(
    tileSize,
    Math.max(1, Math.round(probeHeightPx * sourcePxPerScreenPx)),
  )
  const cx = fx * tileSize
  const cy = fy * tileSize
  const x = Math.min(Math.max(0, Math.round(cx - width / 2)), tileSize - width)
  const y = Math.min(
    Math.max(0, Math.round(cy - height / 2)),
    tileSize - height,
  )
  return { x, y, width, height }
}

export type Point2 = [number, number]

// Maps a point inside an image overlay's footprint to its (u, v) in the image, 0..1.
//
// Projective, not affine: geoCorners come from unprojecting four SCREEN corners through the live
// camera, so at any pitch the footprint is a perspective trapezoid and an affine fit is wrong
// exactly on the plans people tilt. Mapbox refuses to render a non-convex quad, so convexity is
// guaranteed here. Corners are TL, TR, BR, BL, as geoCorners and ImageSource both use.
export function quadToUnitSquare(
  corners: [Point2, Point2, Point2, Point2],
): ((x: number, y: number) => Point2) | null {
  const [[x0, y0], [x1, y1], [x2, y2], [x3, y3]] = corners

  const dx1 = x1 - x2
  const dx2 = x3 - x2
  const dx3 = x0 - x1 + x2 - x3
  const dy1 = y1 - y2
  const dy2 = y3 - y2
  const dy3 = y0 - y1 + y2 - y3

  let a: number
  let b: number
  let d: number
  let e: number
  let g: number
  let h: number

  if (dx3 === 0 && dy3 === 0) {
    // A parallelogram — the perspective terms vanish and this reduces to an affine map.
    a = x1 - x0
    b = x2 - x1
    d = y1 - y0
    e = y2 - y1
    g = 0
    h = 0
  } else {
    const den = dx1 * dy2 - dx2 * dy1
    if (den === 0) return null
    g = (dx3 * dy2 - dx2 * dy3) / den
    h = (dx1 * dy3 - dx3 * dy1) / den
    a = x1 - x0 + g * x1
    b = x3 - x0 + h * x3
    d = y1 - y0 + g * y1
    e = y3 - y0 + h * y3
  }
  const c = x0
  const f = y0

  // Invert the 3x3 that maps (u,v) -> (x,y).
  const m = [a, b, c, d, e, f, g, h, 1]
  const det =
    m[0] * (m[4] * m[8] - m[5] * m[7]) -
    m[1] * (m[3] * m[8] - m[5] * m[6]) +
    m[2] * (m[3] * m[7] - m[4] * m[6])
  if (!det) return null

  const inv = [
    (m[4] * m[8] - m[5] * m[7]) / det,
    (m[2] * m[7] - m[1] * m[8]) / det,
    (m[1] * m[5] - m[2] * m[4]) / det,
    (m[5] * m[6] - m[3] * m[8]) / det,
    (m[0] * m[8] - m[2] * m[6]) / det,
    (m[2] * m[3] - m[0] * m[5]) / det,
    (m[3] * m[7] - m[4] * m[6]) / det,
    (m[1] * m[6] - m[0] * m[7]) / det,
    (m[0] * m[4] - m[1] * m[3]) / det,
  ]

  return (x: number, y: number): Point2 => {
    const w = inv[6] * x + inv[7] * y + inv[8]
    if (!w) return [NaN, NaN]
    return [
      (inv[0] * x + inv[1] * y + inv[2]) / w,
      (inv[3] * x + inv[4] * y + inv[5]) / w,
    ]
  }
}

// The probe rect is expressed in the source's nominal tile space. A retina tile decodes at
// twice that, and sampling the raw rect would read the tile's top-left quadrant instead of
// the label's ground. Requesting 1x instead would be simpler and wrong: mapbox already
// fetched the 2x tile, and that is the HTTP cache entry this read is meant to land on.
export function scaleRectToDecodedSize(
  rect: IPixelRect,
  nominalSize: number,
  decodedSize: number,
): IPixelRect {
  const scale = nominalSize > 0 ? decodedSize / nominalSize : 1
  if (!isFinite(scale) || scale === 1) {
    return rect
  }
  const width = Math.min(
    decodedSize,
    Math.max(1, Math.round(rect.width * scale)),
  )
  const height = Math.min(
    decodedSize,
    Math.max(1, Math.round(rect.height * scale)),
  )
  return {
    x: Math.min(Math.max(0, Math.round(rect.x * scale)), decodedSize - width),
    y: Math.min(Math.max(0, Math.round(rect.y * scale)), decodedSize - height),
    width,
    height,
  }
}
